import {
    call, put, select
} from 'redux-saga/effects';
import CallSession from '../../../models/CallSession';
import { addCallSessionSaga } from '../../callSessions/addCallSession/addCallSession';
import { UserAgent, Inviter, SessionState } from "sip.js";
import { selectUA } from "../../../state/userAgent/userAgentSlice";
import { selectContact } from "../../../state/contact/contactSlice";
import { selectActiveCallCount, selectMaxActiveCalls, setActiveCallId, selectCallSessions } from "../../../state/callSessions/callSessionsSlice";
import { selectUserCredentials } from "../../../state/userCredential/userCredentialSlice";
import { getDisplayName } from "../../../utils/contactUtils";
import { showActiveCallScreen } from '../../../state/activeCallScreen/activeCallScreenSlice';
import putExistingSessionOnHoldSaga from '../../callSessions/putExistingSessionOnHold/putExistingSessionOnHold';
import { updateConferenceParticipantListAction } from '../../conferenceCall/updateConferenceParticipantList/action';
import { endConferenceCallSessionAction } from '../../conferenceCall/endConferenceCallSession/action';
import { playRingbackTone, stopRingbackTone } from '../../../utils/audioUtils';
import { setIsMergingCalls } from '../../../state/activeCallScreen/activeCallScreenSlice';
import { selectCrmEnabled } from '../../../state/config/configSlice';
import { lookupCrmContactAction } from '../../integration/lookupCrmContact/action';
import { showSnackbar } from '../../../state/snackbar/snackbarSlice';
import { selectAudioDevices } from '../../../state/audioDevice/audioDeviceSlice';
import { setLastDialed } from '../../../state/callHistory/callHistorySlice';
import { selectDefaultMicrophone } from '../../../state/audioDevice/audioDeviceSlice';
import { selectAllowedAudioCodecs, selectRemovedAudioCodecs } from '../../../state/config/configSlice';
import stripCodecs from '../../../utils/stripCodecs';
import { normalizeNumber, isExternalNumber } from '../../../utils/phoneUtils';
import removeCodecs from '../../../utils/removeCodecs';
import store from '../../../state/store';

export function* initiateCallSaga({ payload }) {
    const phoneNumber = payload.phoneNumber;
    const isTransferTarget = payload.isTransferTarget;

    if (!isTransferTarget) {
        yield put(setLastDialed(phoneNumber));
    }
    const sourceCallId = payload.sourceCallId;
    const activeCallCount = yield select(selectActiveCallCount)
    const maxCallCount = yield select(selectMaxActiveCalls)
    const defaultMicrophoneId = yield select(selectDefaultMicrophone);
    const { speakers, microphones } = yield select(selectAudioDevices);

    const crmEnabled = yield select(selectCrmEnabled);

    const allowedCodecs = yield select(selectAllowedAudioCodecs);
    const removedCodecs = yield select(selectRemovedAudioCodecs);
    const { defaultCrm } = yield select(selectUserCredentials);

    // Check if microphone and speaker is available
    if (microphones.length === 0 || speakers.length === 0) {
        yield put(showSnackbar({
            message: microphones.length === 0 ?
                'Cannot make call: No microphone detected' :
                'Cannot make call: No speakers detected',
            severity: 'error'
        }));
        return;
    }

    //Soemtimes while merging call, we may initiate call before other 2 calls are dropped. Hence in that case, we still want to join conference call. Therefore, we utilize isConference
    if (activeCallCount >= maxCallCount && !payload.isConference) {
        return;
    }

    const { contact } = yield select(selectContact);
    const { domain } = yield select(selectUserCredentials);
    const UA = yield select(selectUA);
    if (!UA) {
        return;
    }

    const cleanedPhoneNumber = phoneNumber.replace(/[^0-9*#]/g, '');

    // Prevent duplicate call to same number
    const activeSessions = yield select(selectCallSessions);
    const duplicateSession = activeSessions.find(
        session => session.number === cleanedPhoneNumber && session.status !== 'ended'
    );
    if (duplicateSession) {
        yield put(showSnackbar({
            message: 'Call is already active for this number.',
            severity: 'warning'
        }));
        return;
    }

    // If there are any existing call sessions, put that on hold. 
    // If new outgoing call is conference call do nothing; all existing sessions will be cleared automatically.
    if (!payload.isConference) {
        yield call(putExistingSessionOnHoldSaga)
    }

    let directUri = null;
    if (phoneNumber.includes('sip:')) {
        directUri = phoneNumber;
    }

    // Construct the SIP URI
    const uri = directUri || `sip:${cleanedPhoneNumber}@${domain}`;
    const target = UserAgent.makeURI(uri);

    const inviterOptions = {
        earlyMedia: true,
        sessionDescriptionHandlerOptions: {
            constraints: {
                audio: defaultMicrophoneId ? {
                    deviceId: { exact: defaultMicrophoneId }
                } : true,
                video: false
            },
        },
        sessionDescriptionHandlerModifiers: [
            allowedCodecs && allowedCodecs.length > 0 ? stripCodecs(allowedCodecs) : null,
            removedCodecs && removedCodecs.length > 0 ? removeCodecs(removedCodecs) : null,
        ].filter(Boolean), // Remove null values
    }


    const outgoingSession = new Inviter(UA, target, inviterOptions)

    const callSession = new CallSession({
        direction: 'outgoing',
        id: outgoingSession.id,
        number: cleanedPhoneNumber,
        session: outgoingSession,
        status: 'outgoingProgressing',
        isOnHold: false,
        isMuted: false,
        from_tag: outgoingSession.fromTag || outgoingSession.request.fromTag,
        crm: defaultCrm,

    })
    if ((directUri && directUri.includes('adhoc')) || payload.isConference) {
        callSession.displayName = 'Conference Call';
        callSession.conferenceBridgeId = directUri;
        callSession.isConference = true;
    }
    else {
        callSession.displayName = getDisplayName(contact, cleanedPhoneNumber);
        callSession.isConference = false;
    }

    const inviteOptions = {
        requestDelegate: {
            onAccept: (response) => {
                console.debug("onAccept: Call accepted", response);

                if (isTransferTarget && sourceCallId) {
                    const sessions = selectCallSessions(store.getState());
                    const sourceCall = sessions.find(session => session.id === sourceCallId);
                    if (sourceCall && sourceCall.transferState.isTransferSource) {
                        callSession.setAsTransferTarget(sourceCall);
                    }
                }

                if (directUri && directUri.includes('adhoc') && payload.isConference) {
                    callSession.cleanConferenceCall = () => {
                        store.dispatch(endConferenceCallSessionAction({ conferenceUri: directUri }))
                    }

                    // Dispatch an action to update conference participant list
                    store.dispatch(updateConferenceParticipantListAction({ conferenceUri: directUri }));

                    // Set isMergingCalls to false as the conference call is now established
                    store.dispatch(setIsMergingCalls(false));

                }
            },
            onProgress: (response) => {
                console.debug("onProgress: Call is progressing", response);
                if (response.message.statusCode) {
                    const code = response.message.statusCode;
                    if (code === 180 && !callSession.hasEarlyMedia) {
                        playRingbackTone();
                    } else if (code === 183) {
                        callSession.hasEarlyMedia = true;
                        callSession.setupRemoteMedia();
                        stopRingbackTone();
                    }
                }
            },
            onReject: (response) => {
                console.debug("onReject: Call rejected", response);
                stopRingbackTone();
            },
            onRedirect: (response) => {
                console.debug("onRedirect: Call redirected", response);
            },
            onTrying: (response) => {
                console.debug("onTrying: Call trying", response);
            },
        },
        sessionDescriptionHandlerOptions: {
            constraints: {
                audio: defaultMicrophoneId ? {
                    deviceId: { exact: defaultMicrophoneId }
                } : true,
                video: false
            },
        },
    };
    try {
        yield outgoingSession.invite(inviteOptions);
        if (outgoingSession.state === SessionState.Terminated) {
            console.log('Session terminated during invite, not adding to state');
            return;
        }
        yield call(addCallSessionSaga, { payload: callSession }) // add outgoing call session to sessions array
        yield put(setActiveCallId(callSession.id)) //setting id of this session as activeCallId
        yield put(showActiveCallScreen()); // Dispatch an action to show the Active Call Screen

        if (isExternalNumber(cleanedPhoneNumber) && crmEnabled && defaultCrm) {
            yield put(lookupCrmContactAction({
                crm: defaultCrm,
                phone: normalizeNumber(cleanedPhoneNumber),
                sessionId: callSession.id
            }));
        }

    } catch (e) {
        console.error(e);
    }


}